// color-exempt-file — retokenisation 5728fe509 : classes dark:bg-[#hex] ajoutees pour preserver le rendu dark historique (voir memoire ui-themes)
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Check, Share2, Plus, Users, Copy } from "lucide-react";
import { CtaTracker, type CtaVariant } from "@core/services/CtaTracker";
import FinalDateSubscribe from "@core/components/final-date/FinalDateSubscribe";
import { getStableFingerprint } from "@core/lib/guestIdentityService";

// Variante du CTA viral mesurée en baseline (cf. spec
// Docs/SPECS/2026-06-22-cta-viral-tracking-variant-a.md). Pour l'instant figée
// à "A" (copie inchangée) ; B/C + randomisation viendront ensuite.
const CTA_VARIANT: CtaVariant = "A";

interface VoterCompletionScreenProps {
  voterName?: string;
  pollTitle?: string;
  pollSlug?: string;
  pollId?: string | null;
  onViewResults?: () => void;
  onShare?: () => void;
  /** Affiche le CTA « crée ton sondage » (lien mesuré via /api/go). */
  showCreatePoll?: boolean;
  title?: string;
  message?: string;
  /**
   * Propose « Préviens-moi quand la date est fixée » (spec S3) — seulement
   * sur un sondage de dates encore ouvert et sans date fixée.
   */
  offerFinalDateSubscribe?: boolean;
  /** E-mail déjà saisi au vote, pour préremplir (la case reste NON cochée). */
  voterEmail?: string;
}

const VoterCompletionScreen: React.FC<VoterCompletionScreenProps> = ({
  voterName = "Participant",
  pollTitle,
  pollSlug,
  pollId,
  onViewResults,
  onShare,
  showCreatePoll = false,
  title,
  message,
  offerFinalDateSubscribe = false,
  voterEmail,
}) => {
  const { t } = useTranslation("polls");

  // CTA affiché : `shown`, dédoublonné en base par participant et par sondage
  // (clé = jeton de vote aléatoire, haché et salé par sondage côté serveur).
  useEffect(() => {
    if (!showCreatePoll) return;
    let cancelled = false;
    void getStableFingerprint().then((participant) => {
      if (cancelled) return;
      void CtaTracker.track("shown", {
        pollId,
        variant: CTA_VARIANT,
        participant: participant || null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [showCreatePoll, pollId]);

  // Le clic est compté par /api/go AVANT la redirection : un lien simple
  // survit à la navigation, y compris dans le navigateur intégré de WhatsApp.
  const createPollHref = CtaTracker.goHref({
    to: "create",
    ref: "vote",
    pollId,
    variant: CTA_VARIANT,
  });
  const [wantsOtherVotes, setWantsOtherVotes] = useState(false);

  // Toast simple intégré
  const showToast = (
    title: string,
    description?: string,
    variant: "default" | "destructive" = "default",
  ) => {
    // Créer un élément toast simple
    const toast = document.createElement("div");
    toast.className = `fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
      variant === "destructive"
        ? "bg-destructive text-destructive-foreground"
        : "bg-success text-success-foreground"
    }`;
    toast.innerHTML = `
      <div class="font-medium">${title}</div>
      ${description ? `<div class="text-sm opacity-90">${description}</div>` : ""}
    `;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  // Écran de fin de vote — suit toujours --primary (le thème actif), jamais
  // le type de sondage (voir memoire ui-themes).
  const theme = {
    bg: "bg-primary/20",
    text: "text-primary",
    button: "bg-primary hover:bg-primary/90",
    alertBg: "bg-primary/10",
    alertBorder: "border-primary/30",
    alertText: "text-primary",
    alertTextLight: "text-primary/90",
    outlineButton: "text-primary border-primary/30 hover:bg-primary/10",
  };

  const handleCopyLink = async () => {
    if (!pollSlug) return;

    const link = `${window.location.origin}/poll/${pollSlug}/vote`;
    const shareTitle = pollTitle
      ? t("completion.share.titleWithName", { title: pollTitle })
      : t("completion.share.titleDefault");
    const shareText = pollTitle
      ? t("completion.share.textWithName", { title: pollTitle })
      : t("completion.share.textDefault");

    // Utilise l'API Web Share si disponible (déclenche le menu natif mobile : WhatsApp, SMS, etc.)
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function"
    ) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: link,
        });
        return;
      } catch (error) {
        // L'utilisateur a annulé le partage — ne pas afficher d'erreur
        if ((error as DOMException)?.name === "AbortError") {
          return;
        }
        // Sur les autres erreurs, on tombe sur le fallback clipboard
      }
    }

    // Fallback : copier dans le presse-papiers
    try {
      await navigator.clipboard.writeText(link);
      showToast(
        t("completion.toast.linkCopied.title"),
        t("completion.toast.linkCopied.description"),
      );
    } catch (error) {
      showToast(
        t("completion.toast.error.title"),
        t("completion.toast.linkCopyFailed"),
        "destructive",
      );
    }
  };

  return (
    <div className="min-h-screen-safe bg-background dark:bg-[#0a0a0a]">
      <div className="flex items-center justify-center p-4 min-h-[calc(100dvh-80px)]">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center w-full max-w-md"
        >
          <div className="bg-card dark:bg-[#1e1e1e] rounded-2xl p-8 shadow-sm border border-border">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className={`w-20 h-20 ${theme.bg} rounded-full flex items-center justify-center mx-auto mb-6`}
            >
              <Check className={`w-10 h-10 ${theme.text}`} />
            </motion.div>

            <h2 className="text-2xl font-bold mb-2 text-foreground">
              {title || t("completion.title")}
            </h2>
            <p className="text-muted-foreground mb-8">
              {message || t("completion.message", { name: voterName })}
            </p>

            {offerFinalDateSubscribe && pollId && (
              <FinalDateSubscribe
                pollId={pollId}
                defaultEmail={voterEmail}
                className="mb-6"
              />
            )}

            {/* Actions principales */}
            <div className="flex flex-col gap-3 items-center mb-6">
              {onViewResults && (
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={onViewResults}
                  className={`${theme.button} text-primary-foreground font-medium py-3 px-6 rounded-xl w-full transition-colors shadow-sm`} // color-exempt
                  data-testid="view-results-button"
                >
                  {t("completion.viewResults")}
                </motion.button>
              )}

              {showCreatePoll && (
                <motion.a
                  whileTap={{ scale: 0.95 }}
                  href={createPollHref}
                  data-testid="create-poll-cta"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-3 px-6 rounded-xl w-full transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t("completion.createPoll")}
                </motion.a>
              )}

              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleCopyLink}
                className={`${theme.outlineButton} font-medium py-3 px-6 rounded-xl w-full transition-colors flex items-center justify-center gap-2`}
              >
                <Share2 className="w-4 h-4" />
                {t("completion.sharePoll")}
              </motion.button>
            </div>

            {/* Options additionnelles */}
            <div className="space-y-4">
              {/* Option autres votes */}
              {/* <div className={`p-4 ${theme.alertBg} border ${theme.alertBorder} rounded-lg`}>
                <div className="flex items-center gap-2 mb-3">
                  <Users className={`w-4 h-4 ${theme.alertText}`} />
                  <span className={`text-sm font-medium ${theme.alertText}`}>Découvrir d'autres sondages</span>
                </div>
                <p className={`text-sm ${theme.alertTextLight} mb-3`}>
                  Recevez une notification quand de nouveaux sondages intéressants sont publiés
                </p>
                <label className="flex items-center gap-2 text-sm text-gray-400">
                  <input
                    type="checkbox"
                    checked={wantsOtherVotes}
                    onChange={(e) => setWantsOtherVotes(e.target.checked)}
                    className="rounded border-gray-600 bg-[#2a2a2a] text-blue-600 focus:ring-blue-500"
                  />
                  Je m'intéresse aux autres sondages
                </label>
                {wantsOtherVotes && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    className={`${theme.button} text-white text-sm py-2 px-4 rounded-lg transition-colors mt-2`}
                  >
                    S'inscrire aux notifications
                  </motion.button>
                )}
              </div> */}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VoterCompletionScreen;
